import React, { useState, useRef, useEffect } from 'react';
import { openExternalUrl } from '@/utils/url-utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  CloudUpload,
  Download,
  X,
  TextCursorInput,
  Plus,
  TriangleAlert,
  CircleAlert,
} from 'lucide-react';
import { NodeParam, NodeOutputPort } from '@/types/node-types';
import {
  getCategoryConfig,
  getFileIcon,
  getFileIconBackgroundColor,
  FILE_TYPE_COLORS,
} from '@/constants/node-styling';
import { NodeHandle } from './node-handle';
import { FileIconTile } from '@/pages/workflow/component/file-icon-tile';
import { Modal } from '@/components/modal';
import { Dropzone } from '@/components/dropzone';
import { LoadingSpinner } from '@/components/loading-spinner';
import {
  uploadFile,
  isLargeUpload,
  FileUploadResponse,
} from '@/services/workflow/upload-file';
import { useNotification } from '@/components/notifications';
import { truncateFilename } from '@/utils/text-utils';
import { PromptEnhancer } from './prompt-enhancer';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import {
  useUploadStore,
  useNodeUpload,
  UploadItem,
} from '@/stores/use-upload-store';
import { UploadBar } from '@/pages/workflow/component/upload/upload-ui';
import { cn } from '@/lib/utils';
import {
  FILE_INPUT_MEDIA_MAX_SIZE_MB,
  FILE_INPUT_MAX_SIZE_MB,
  FILE_INPUT_ACCEPT,
} from '@/constants/file-upload';

type PreviewMode = 'file' | 'text';

interface NodeUploadRowProps {
  upload: UploadItem;
  hideOutputHandle: boolean;
  configHandleClass?: string;
  handle?: React.ReactNode;
  onCancel: () => void;
  onDismiss: () => void;
}

const NodeUploadRow: React.FC<NodeUploadRowProps> = ({
  upload,
  hideOutputHandle,
  configHandleClass,
  handle,
  onCancel,
  onDismiss,
}) => {
  const isFailed = upload.phase === 'failed';
  const isProcessing = upload.phase === 'processing';

  const containerClass = cn(
    'flex items-center w-full gap-1.5 py-1.5 rounded-xl relative border-2',
    hideOutputHandle
      ? 'bg-white dark:bg-neutral-800 px-1.5 border-transparent'
      : cn(
          'mb-[16px] bg-muted-background pl-0.5 pr-1 border-transparent',
          configHandleClass,
        ),
    isFailed && 'border-destructive/40! bg-destructive/5',
  );

  return (
    <div className={containerClass}>
      {handle}
      {isFailed ? (
        <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-destructive/15 text-destructive'>
          <CircleAlert className='size-4' />
        </span>
      ) : (
        <FileIconTile
          icon={getFileIcon(upload.filename)}
          backgroundColor={getFileIconBackgroundColor(upload.filename)}
          size='small'
          iconColor='#ffffff'
        />
      )}

      <div className='flex-1 min-w-0 pl-1'>
        <span
          className='block text-sm font-medium truncate text-black dark:text-white!'
          title={upload.filename}
        >
          {upload.filename}
        </span>
        {isFailed ? (
          <span className='block text-[11px] text-destructive truncate mt-0.5'>
            {upload.error || 'Upload failed'}
          </span>
        ) : (
          <div className='flex items-center gap-2 mt-1'>
            <UploadBar
              percent={upload.percent}
              indeterminate={isProcessing || upload.percent === 0}
              className='flex-1'
            />
            <span className='text-[11px] font-mono font-semibold tabular-nums text-primary'>
              {isProcessing
                ? 'Finishing'
                : upload.percent === 0
                  ? 'Starting'
                  : `${upload.percent}%`}
            </span>
          </div>
        )}
      </div>

      {isFailed ? (
        <Button
          variant='ghost'
          size='icon'
          className='ml-auto size-6 shrink-0 text-muted-foreground'
          title='Dismiss'
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          <X className='size-3.5' />
        </Button>
      ) : (
        <Button
          variant='ghost'
          size='icon'
          className='size-6 shrink-0 text-muted-foreground'
          title='Cancel upload'
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
        >
          <X className='size-3.5' />
        </Button>
      )}
    </div>
  );
};

interface PreviewSlotCardProps {
  nodeId: string;
  field: NodeParam;
  /** When omitted (e.g. errors sidebar), config uses `field.field_id` and no handle is shown if `hideOutputHandle`. */
  outputPort?: NodeOutputPort;
  workflowId?: string;
  handleParameterChange: (fieldId: string, value: unknown) => void;
  mode: PreviewMode;
  nodeName?: string;
  nodeType?: string;
  isLocked?: boolean;
  /** Omit React Flow output handle — use with panel surface (e.g. workflow issues tab). */
  hideOutputHandle?: boolean;
}

export const PreviewSlotCard: React.FC<PreviewSlotCardProps> = ({
  nodeId,
  field,
  outputPort,
  workflowId,
  handleParameterChange,
  mode,
  nodeName = '',
  nodeType = '',
  isLocked = false,
  hideOutputHandle = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [textValue, setTextValue] = useState('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [textOriginalText, setTextOriginalText] = useState('');
  const originalTextRef = useRef<string>('');
  const isChangeFromEnhancementRef = useRef(false);
  const notification = useNotification();
  const { currentWorkflow } = useWorkflowStore();
  const startUpload = useUploadStore((state) => state.startUpload);
  const cancelUpload = useUploadStore((state) => state.cancelUpload);
  const removeUpload = useUploadStore((state) => state.removeUpload);
  const upload = useNodeUpload(nodeId, field.field_id);
  const { fieldErrors, clearMissingFile, missingFiles } =
    useWorkflowValidationStore();
  const hasMissingFileError = missingFiles.some(
    (e) => e.nodeId === nodeId && e.fieldId === field.field_id,
  );

  const configPortId = outputPort?.id ?? field.field_id;

  // Get category-specific styling
  const config = getCategoryConfig(
    'output',
    configPortId,
    mode === 'file' ? 'Upload Files' : 'Input Text',
    outputPort?.port_type.accepted_types || [],
  );

  // Check if this field has a validation error
  const hasValidationError = fieldErrors.some(
    (error) =>
      error.nodeId === nodeId &&
      (error.fieldName === (field.display_name || field.field_id) ||
        error.fieldId === field.field_id ||
        (outputPort &&
          (error.fieldName === outputPort.id ||
            error.fieldName === outputPort.display_name))),
  );

  // Initialize text value from field
  useEffect(() => {
    if (mode === 'text') {
      const initialValue = String(field.value || '');
      setTextValue(initialValue);
      if (!originalTextRef.current && initialValue) {
        originalTextRef.current = initialValue;
        setTextOriginalText(initialValue);
      }
    }
  }, [field.value, mode]);

  // Parse the uploaded file info from field.value (for file mode)
  const uploadedFile = React.useMemo(() => {
    if (mode !== 'file') return null;
    try {
      const value = field.value;
      if (value && typeof value === 'object' && 'success' in value) {
        const uploadResponse = value as FileUploadResponse;
        if (uploadResponse.success && uploadResponse.file) {
          return {
            filename: uploadResponse.file.filename,
            fileSize: uploadResponse.file.file_size,
            downloadUrl: uploadResponse.download_url,
          };
        }
        // Imported workflow references a file that is not on the server: still show name/size
        if (uploadResponse.file?.filename) {
          return {
            filename: uploadResponse.file.filename,
            fileSize: uploadResponse.file.file_size ?? 0,
            downloadUrl: uploadResponse.download_url,
          };
        }
      }
    } catch {
      // Ignore parsing errors
    }
    return null;
  }, [field.value, mode]);

  // File upload handler
  const handleDropAccepted = async (files: File[]) => {
    // Prevent upload if workflow is locked
    if (isLocked) {
      return;
    }

    // Validate required parameters
    if (!workflowId || !nodeId) {
      console.error('Missing required parameters for file upload:', {
        workflowId,
        nodeId,
      });
      return;
    }

    // Only process the first file for single file upload
    if (files.length === 0) {
      console.error('No files provided for upload');
      return;
    }

    const file = files[0]; // Take only the first file

    // Large files take the slow presigned/streaming path — hand them to the
    // upload store so they run in the background with progress, cancel, and
    // retry on the node row, the Configure panel, and the floating widget.
    if (isLargeUpload(file)) {
      startUpload({
        nodeId,
        fieldId: field.field_id,
        workflowId,
        file,
        nodeName: nodeName || 'File Input',
      });
      setIsModalOpen(false);
      return;
    }

    // Small files finish quickly via single-shot — keep the simple in-modal
    // spinner rather than the background progress UI.
    setIsUploading(true);
    try {
      const uploadResponse = await uploadFile(file, workflowId, nodeId);
      if (uploadResponse.success && uploadResponse.file) {
        handleParameterChange(field.field_id, uploadResponse);
        clearMissingFile(nodeId, field.field_id);
        setIsModalOpen(false);
      } else {
        notification.error(
          `File upload failed: ${uploadResponse.message || 'Unknown error'}`,
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      notification.error(`File upload failed: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Text modal handlers
  const handleTextModalOpen = () => {
    const currentValue = String(field.value || '');
    setTextValue(currentValue);
    originalTextRef.current = currentValue;
    setTextOriginalText(currentValue);
    setIsModalOpen(true);
  };

  const handleTextModalClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsModalOpen(false);
  };

  const handleTextModalSave = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    handleParameterChange(field.field_id, textValue);
    // Update original text ref and state when user manually edits
    if (!isEnhancing) {
      originalTextRef.current = textValue;
      setTextOriginalText(textValue);
    }
    setIsModalOpen(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Truncate text to single line for display
  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return '';
    const singleLine = text.replace(/\n/g, ' ').trim();
    if (singleLine.length <= maxLength) return singleLine;
    return `${singleLine.slice(0, maxLength)}...`;
  };

  const displayValue = mode === 'text' ? String(field.value || '') : null;
  const hasTextValue = displayValue && displayValue.trim().length > 0;

  const showUploadState =
    mode === 'file' && !!upload && upload.phase !== 'done';

  const outputHandle =
    !hideOutputHandle && outputPort ? (
      <NodeHandle
        key={outputPort.id}
        port={{
          id: outputPort.id,
          display_name: outputPort.display_name,
          info: outputPort.info || '',
          port_type: outputPort.port_type,
        }}
        nodeId={nodeId}
        direction='output'
        config={config}
        isLocked={isLocked}
        hasValidationError={hasValidationError}
      />
    ) : null;

  return (
    <>
      <div className={`${hideOutputHandle ? 'px-0' : 'px-4'} relative`}>
        {!hideOutputHandle && hasMissingFileError && !showUploadState && (
          <span
            className='absolute -top-1 right-3 z-10 flex size-5 items-center justify-center rounded-full bg-red-500'
            role='status'
            aria-label='Missing file'
          >
            <TriangleAlert className='size-3 text-white' strokeWidth={2.5} />
          </span>
        )}
        {showUploadState && upload ? (
          <NodeUploadRow
            upload={upload}
            hideOutputHandle={hideOutputHandle}
            configHandleClass={config.handleClass}
            handle={outputHandle}
            onCancel={() => cancelUpload(upload.uploadId)}
            onDismiss={() => removeUpload(upload.uploadId)}
          />
        ) : (
          <div
            role='button'
            tabIndex={isLocked ? -1 : 0}
            aria-disabled={isLocked}
            className={cn(
              'group inline-flex items-center justify-center w-full h-full gap-1 py-1.5 rounded-xl relative transition-colors duration-200 border-2 text-sm font-medium',
              hideOutputHandle
                ? cn(
                    'mb-0 bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 px-1.5',
                    hasValidationError
                      ? 'border-red-500'
                      : 'border-transparent',
                  )
                : cn(
                    'mb-[16px] bg-muted-background hover:bg-neutral-200! dark:hover:bg-neutral-600! border-transparent pl-0.5 pr-1',
                    hasValidationError
                      ? 'border-red-500'
                      : 'border-transparent',
                    config.handleClass,
                  ),
              isLocked && 'cursor-not-allowed opacity-60 pointer-events-none',
              !isLocked && 'cursor-pointer',
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (isLocked) return;
              if (mode === 'file') {
                setIsModalOpen(true);
              } else {
                handleTextModalOpen();
              }
            }}
            onKeyDown={(e) => {
              if (isLocked) return;
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              e.stopPropagation();
              if (mode === 'file') {
                setIsModalOpen(true);
              } else {
                handleTextModalOpen();
              }
            }}
          >
            {outputHandle}
            {/* Icon tile */}
            {mode === 'file' ? (
              uploadedFile ? (
                <FileIconTile
                  icon={getFileIcon(uploadedFile.filename)}
                  backgroundColor={getFileIconBackgroundColor(
                    uploadedFile.filename,
                  )}
                  size='small'
                  iconColor='#ffffff'
                />
              ) : (
                <FileIconTile
                  icon={CloudUpload}
                  backgroundColor='#64748B'
                  size='small'
                  iconColor='#ffffff'
                />
              )
            ) : (
              <FileIconTile
                icon={TextCursorInput}
                backgroundColor={
                  hasTextValue ? FILE_TYPE_COLORS.text : '#64748B'
                }
                size='small'
                iconColor='#ffffff'
              />
            )}
            {/* Label */}
            <div
              className='flex-1 min-w-0 flex flex-col items-start pl-1'
              title={
                mode === 'file'
                  ? uploadedFile?.filename
                  : hasTextValue
                    ? displayValue
                    : undefined
              }
            >
              {mode === 'file' ? (
                uploadedFile ? (
                  <>
                    <span className='text-sm font-medium max-w-full text-black dark:text-white! truncate'>
                      {hideOutputHandle
                        ? uploadedFile.filename
                        : truncateFilename(uploadedFile.filename, 20)}
                    </span>
                    <span className='text-xs text-muted-foreground'>
                      {formatFileSize(uploadedFile.fileSize)}
                    </span>
                  </>
                ) : (
                  <span className='text-sm font-medium dark:text-white!'>
                    Upload Files
                  </span>
                )
              ) : hasTextValue ? (
                <span className='text-sm font-medium max-w-full text-black dark:text-white! truncate'>
                  {truncateText(displayValue, 30)}
                </span>
              ) : (
                <span className='text-sm font-medium dark:text-white!'>
                  Input Text
                </span>
              )}
            </div>
            {/* Required indicator when validation error */}
            {hasValidationError ? (
              <div className='ml-auto flex items-center gap-2 shrink-0'>
                <span
                  className='text-xs font-normal text-red-500'
                  title='Required'
                >
                  Required
                </span>
                <Plus className='w-4 h-4 shrink-0 text-red-500' />
              </div>
            ) : (
              !hideOutputHandle && (
                <>
                  {/* Download/Remove buttons for file, Remove button for text */}
                  {mode === 'file' && uploadedFile && (
                    <div className='ml-auto flex items-center'>
                      {uploadedFile.downloadUrl && (
                        <Button
                          variant='link'
                          size='icon'
                          className='h-7 w-7 text-muted-foreground hover:text-blue-600'
                          title='Download file'
                          onClick={(e) => {
                            e.stopPropagation();
                            openExternalUrl(uploadedFile.downloadUrl);
                          }}
                        >
                          <Download className='h-4 w-4' />
                        </Button>
                      )}
                      <Button
                        variant='link'
                        size='icon'
                        className='h-7 w-7 text-muted-foreground hover:text-destructive'
                        title='Remove file'
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isLocked) return;
                          handleParameterChange(field.field_id, '');
                        }}
                        disabled={isLocked}
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </div>
                  )}
                  {mode === 'text' && hasTextValue && (
                    <div className='ml-auto flex items-center'>
                      <Button
                        variant='link'
                        size='icon'
                        className='h-7 w-7 text-muted-foreground hover:text-destructive'
                        title='Clear text'
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isLocked) return;
                          handleParameterChange(field.field_id, '');
                        }}
                        disabled={isLocked}
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </div>
                  )}
                </>
              )
            )}
          </div>
        )}
      </div>

      {/* File Upload Modal */}
      {mode === 'file' && (
        <Modal
          isDialogOpen={isModalOpen}
          setIsDialogOpen={setIsModalOpen}
          title={<span>Upload File</span>}
          footer={
            <Button
              variant='outline'
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(false);
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
          }
        >
          <div
            className='flex flex-col gap-4 mt-4'
            onClick={(e) => e.stopPropagation()}
          >
            {isUploading ? (
              <div className='flex flex-col items-center justify-center py-8 gap-4'>
                <LoadingSpinner />
                <span className='text-muted-foreground'>Uploading file...</span>
              </div>
            ) : (
              <div className='space-y-2'>
                <p className='text-sm text-muted-foreground'>
                  Select a single file to upload
                </p>
                <Dropzone
                  handleUpload={handleDropAccepted}
                  isLoading={isUploading}
                  acceptType={FILE_INPUT_ACCEPT}
                  maxSize={FILE_INPUT_MAX_SIZE_MB}
                  audioMaxSize={FILE_INPUT_MEDIA_MAX_SIZE_MB}
                  multiple={false}
                  preventCanvasInterference={true}
                />
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Text Input Modal */}
      {mode === 'text' && (
        <Modal
          isDialogOpen={isModalOpen}
          setIsDialogOpen={setIsModalOpen}
          title={<span>Edit {field.display_name || 'Text'}</span>}
          footer={
            <div className='flex gap-2' onClick={(e) => e.stopPropagation()}>
              <Button variant='outline' onClick={handleTextModalClose}>
                Cancel
              </Button>
              <Button onClick={handleTextModalSave}>Save</Button>
            </div>
          }
        >
          <div
            className='flex flex-col gap-4 mt-4 max-h-[70vh] overflow-hidden relative'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='relative'>
              <Textarea
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                placeholder={`Enter ${field.display_name || 'text'} ...`}
                className='w-full min-h-[300px] max-h-[60vh] resize-y overflow-y-auto pr-6'
                autoFocus
              />
              {/* Prompt Enhancer in Modal - with sliding label */}
              <PromptEnhancer
                value={textValue}
                onChange={(newValue) => {
                  // Mark this change as from enhancement
                  isChangeFromEnhancementRef.current = true;
                  setTextValue(newValue);
                }}
                onEnhancementStart={(originalText) => {
                  // Capture original text BEFORE enhancement starts
                  originalTextRef.current = originalText;
                  setTextOriginalText(originalText);
                }}
                onEnhancingChange={(enhancing) => {
                  setIsEnhancing(enhancing);
                }}
                variant='slide'
                className='absolute right-1 top-1 z-10'
                workflowName={currentWorkflow?.name || ''}
                workflowDescription={currentWorkflow?.description || ''}
                nodeType={nodeType}
                nodeName={nodeName}
                originalText={textOriginalText}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
